import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, IconButton,
  InputAdornment, TextField, Modal, Box, Typography, Select, FormControl, Alert, CircularProgress,
  Chip, Radio, ListItemText
} from '@mui/material';
import { Delete as DeleteIcon, Close as CloseIcon, OpenInBrowser, Edit as EditIcon, CloseOutlined } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useApi } from 'context/ApiProvider';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { showAlertDialog } from 'components/Dialog/dialog';
import { AdminUserData } from 'models/AdminUserData';
import { fetchAdminUsers, fetchAllGroups } from 'services/adminService';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { AdminGroupSelectItem } from 'models/AdminGroupSelectItem';
import { PaginatedUserSearchRequestDto, UserSortType } from 'models/PaginatedUserSearchRequestDto';
import DownIcon from '@mui/icons-material/KeyboardArrowDown';
import UpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAuth } from 'context/AuthContext';


interface SettingData {
  id: number;
  name: string;
  configFile: string;
}
interface CalibData {
  userDeviceId: number;
  name: string;
}

const OwnersTable: React.FC = () => {
  var authenticatedUser = useAuth().user;
  const { isDarkMode } = useAppTheme();
  const api = useApi();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [userData, setUserData] = useState<AdminUserData[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDeviceModal, setOpenDeviceModal] = useState(false);
  const [selectedUserPrinterSettings, setSelectedUserPrinterSettings] = useState<SettingData[]>([]);
  const [settingName, setDeviceName] = useState('');
  const [configFile, setConfigFile] = useState<File | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [refreshUsers, setRefreshUsers] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openFilterMenu = Boolean(anchorEl);

  const [openCalibModal, setOpenCalibModal] = useState(false);
  const [calibName, setCalibName] = useState('');
  const [calibFile, setCalibFile] = useState<File | null>(null);
  const [calibLoading, setCalibLoading] = useState(false);
  const [selectedUserCalib, setSelectedUserCalib] = useState<CalibData | null>(null);
  const pageSize = 10;
  const totalPages = useRef(1);
  const [filterStatus, setFilterStatus] = useState<'All' | 'Active' | 'Inactive'>('All');
  const [groupSelectItems, setGroupSelectItems] = useState<AdminGroupSelectItem[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AdminGroupSelectItem | null>(null);
  const [groupFilterAnchor, setGroupFilterAnchor] = useState<null | HTMLElement>(null);

  const [editingDeviceId, setEditingDeviceId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sortColumnName, setSortColumnName] = useState<UserSortType>(null);
  const [sortDesc, setSortDesc] = useState<boolean | null>(null);
  const [isDeleteEnabled, setDeleteEnabled] = useState<boolean>(true);
  const [isGroupEditEnabled, setGroupEditEnabled] = useState<boolean>(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const userRequestDto = new PaginatedUserSearchRequestDto({
          pageNumber,
          pageSize,
          searchTerm,
          groupName: selectedGroup?.groupName ?? null,
          status: filterStatus === 'All' ? null : filterStatus,
          sortBy: sortColumnName,
          sortDescending: sortDesc,
        });
        const res = await fetchAdminUsers(api, userRequestDto);
        if (!res) {
          setError('No response from server!');
          totalPages.current = 0;
          setTotalUsers(0);
          setUserData([]);
          setHasNextPage(false);
          return;
        }
        setTotalUsers(res.totalRecords ?? 0);
        if (!res?.data || res.data.length < 1) {
          setError('No users found for this page.');
          totalPages.current = 0;
          setUserData([]);
          setHasNextPage(false);
          return;
        }
        totalPages.current = res.totalPages;
        setPageNumber(res.pageNumber);
        setHasNextPage(res.pageNumber < res.totalPages);
        const mappedData: AdminUserData[] = Array.isArray(res?.data) && res.data.length > 0
          ? res.data
          : [];
        setUserData(mappedData);
        setError(mappedData.length < 1 ? 'No users found for this page' : null);
      } catch (err) {
        setError('Failed to fetch users');
        console.error(err);
        totalPages.current = 0;
        setTotalUsers(0);
        setUserData([]);
        setHasNextPage(false);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [api, pageNumber, searchTerm, selectedGroup, filterStatus, sortColumnName, sortDesc, refreshUsers]);

  const applyUserAccess = () => {
    if (authenticatedUser?.group?.IsHub) {
      setDeleteEnabled(false);
      setGroupEditEnabled(false);
    } else {
      setDeleteEnabled(true);
      setGroupEditEnabled(true);
    }
  }

  useEffect(() => {
    getGroups().then((groups) => {
      setGroupSelectItems(groups);
    });
    applyUserAccess();
  }, [])

  const getGroups = async (): Promise<AdminGroupSelectItem[]> => {
    try {
      const groups = await fetchAllGroups(api);
      return groups;
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch group list from server.', { duration: 4000 });
      return [];
    }
  }

const handleDeleteUser = async (userId: number) => {
  setSelected(userId);
  try {
    const confirmed = await showAlertDialog({
      title: 'Remove User',
      description: 'Are you sure you want to remove this user?',
      agreeText: 'Yes',
      disagreeText: 'No',
    });
    if (!confirmed) return;

      const res = await api.delete(`/api/admin/users/${userId}`);
      if (!res.ok) throw new Error('Failed to delete user');

      setUserData(userData.filter(user => user.id !== userId));
      setRefreshUsers((value) => value + 1);
      toast.success('User removed successfully', { duration: 4000 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove user', { duration: 4000 });
    }
  };


  const handleStatusChange = async (userId: number, status: 'Active' | 'Inactive') => {
    try {
      const res = await api.patch('/api/admin/users/status', { userId, status });
      if (res.status === 200) {
        toast.success(`User has been ${status === 'Active' ? 'activated' : 'deactivated'} successfully.`, { duration: 5000 });
        setUserData(userData.map(user =>
          user.id === userId ? { ...user, isActive: status } : user
        ));
        setRefreshUsers((value) => value + 1);
      }
      setError(null);
    } catch (err) {
      setError('Failed to update status');
      console.error(err);
    }
  };

  const handleGroupChange = async (userId: number, groupId: string | number) => {
    try {
      const res = await api.patch('/api/admin/users/', { userId, groupId });
      if (res.status === 200) {
        toast.success(`User group saved successfully.`, { duration: 5000 });
        setUserData(userData.map(user =>
          user.id === userId ? { ...user, groupId: typeof groupId === 'string' ? parseInt(groupId) : groupId } : user
        ));
        setRefreshUsers((value) => value + 1);
      }
      setError(null);
    } catch (err) {
      setError('Failed to update group');
      console.error(err);
    }
  };

  const handleOpenDeviceModal = async (userId: number) => {
    setSelected(userId);
    const selecteduser = userData.filter(a => a.id == userId).at(0);
    try {
      setDeviceLoading(true);
      let res = null;
      if (!selecteduser?.isAdmin)
        res = await api.get(`/api/admin/users/${userId}/printersetting`);
      else
        res = await api.get(`/api/admin/defaultprintersetting`);
      setSelectedUserPrinterSettings(Array.isArray(res.body) ? res.body : []);
      setOpenDeviceModal(true);
      setError(null);
    } catch (err) {
      setError('Failed to fetch devices');
      console.error(err);
      setSelectedUserPrinterSettings([]);
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleAddDevice = async (userId: number) => {
    if (!settingName || !configFile || !selected) return;
    const selecteduser = userData.filter(a => a.id == selected).at(0);
    const formData = new FormData();
    formData.append('name', settingName);
    formData.append('Settings', configFile);
    if (selecteduser && !selecteduser.isAdmin) formData.append('userId', userId.toString());
    try {
      setDeviceLoading(true);
      await api.post(`/api/admin/users/printersetting`, formData);

      let res = null;
      if (!selecteduser?.isAdmin)
        res = await api.get(`/api/admin/users/${userId}/printersetting`);
      else
        res = await api.get(`/api/admin/defaultprintersetting`);
      setSelectedUserPrinterSettings(Array.isArray(res.body) ? res.body : []);
      setDeviceName('');
      setConfigFile(null);
      setError(null);
    } catch (err) {
      setError('Failed to add device');
      console.error(err);
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleDeleteDevice = async (userId: number, deviceId: number) => {
    const result = await showAlertDialog({
      title: "Removing a Printer Setting",
      description: "Are you sure to remove this setting?",
      agreeText: "Yes",
      disagreeText: "No",
    });

    if (!result) return;
    try {
      setDeviceLoading(true);
      const res = await api.delete(`/api/admin/printersetting/${deviceId}`);
      if (!res.ok) { setError('Failed to delete device'); return; }
      setSelectedUserPrinterSettings(selectedUserPrinterSettings.filter(device => device.id !== deviceId));
      setError(null);
    } catch (err) {
      setError('Failed to delete device');
      console.error(err);
    } finally {
      setDeviceLoading(false);
    }
  };
  const handleEditDevice = async (userId: number, deviceId: number) => {
    // try {
    //   setDeviceLoading(true);
    //   const res = await api.delete(`/api/admin/printersetting/${deviceId}`);
    //   if (!res.ok) { setError('Failed to delete device'); return; }
    //   setSelectedUserPrinterSettings(selectedUserPrinterSettings.filter(device => device.id !== deviceId));
    //   setError(null);
    // } catch (err) {
    //   setError('Failed to delete device');
    //   console.error(err);
    // } finally {
    //   setDeviceLoading(false);
    // }
    const selectedDevice = selectedUserPrinterSettings.filter(device => device.id === deviceId);
    setEditingDeviceId(selectedDevice[0].id);
    setDeviceName(selectedDevice[0].name);   // put name into TextField
    setConfigFile(null);          // optional: require re-upload
    setIsEditMode(true);
  };

  const handleUpdateDevice = async (userId: number, deviceId: number) => {
    if (!settingName) return;

    const selecteduser = userData.find(a => a.id === userId);
    const formData = new FormData();
    formData.append('name', settingName);
    formData.append('id', deviceId.toString());
    if (configFile) {
      formData.append('Settings', configFile);
    }
    if(!selecteduser?.isAdmin){
      formData.append('userId',  selecteduser!.id!.toString());
    }

    try {
      setDeviceLoading(true);
      const res1 = await api.post(`/api/admin/users/printersetting/`, formData);
      if(!res1.ok){ setError('Failed update device'); return; }

      // refresh list     
      const res = selecteduser?.isAdmin
        ? await api.get(`/api/admin/defaultprintersetting`)
        : await api.get(`/api/admin/users/${userId}/printersetting`);

      setSelectedUserPrinterSettings(Array.isArray(res.body) ? res.body : []);

      // reset form
      setDeviceName('');
      setConfigFile(null);
      setEditingDeviceId(null);
      setIsEditMode(false);
      setError(null);

      toast.success('Printer setting updated successfully');
    } catch (err) {
      console.error(err);
      setError('Failed to update device');
    } finally {
      setDeviceLoading(false);
    }
  };


  const handleOpenCalibModal = async (userId: number) => {
    setSelected(userId);
    try {
      setCalibLoading(true);
      const res = await api.get(`/api/user/${userId}/pressure_calibration/false`);
      const data = res.body;
      setSelectedUserCalib(data && Object.keys(data).length ? (data as CalibData) : null);
      setOpenCalibModal(true);
      setError(null);
    } catch (err) {
      setError('Failed to fetch pressure calibration files');
      console.error(err);
      setSelectedUserCalib(null);
    } finally {
      setCalibLoading(false);
    }
  };

  const handleAddCalib = async (userId: number) => {
    if (!calibName || !calibFile || !selected) return;
    const formData = new FormData();
    formData.append('Name', calibName);
    formData.append('ConfigFile', calibFile);
    formData.append('userId', userId.toString());
    try {
      setCalibLoading(true);
      await api.post(`/api/admin/users/calib`, formData);

      const res = await api.get(`/api/user/${userId}/pressure_calibration/false`);
      const data = res.body;
      setSelectedUserCalib(
        data && typeof data === 'object' && Object.keys(data).length
          ? (data as CalibData)
          : null
      );
      setCalibName('');
      setCalibFile(null);
      setError(null);
    } catch (err) {
      setError('Failed to upload calibration file');
      console.error(err);
    } finally {
      setCalibLoading(false);
    }
  };

  const handleDeleteCalib = async (userId: number, deviceId: number) => {
    try {
      setCalibLoading(true);
      const res = await api.delete(`/api/admin/users/calib/${deviceId}`);
      if (!res.ok) { setError('Failed to delete pressure calibration'); return; }
      setSelectedUserCalib(null);
      setError(null);
    } catch (err) {
      setError('Failed to delete pressure calibration');
      console.error(err);
    } finally {
      setCalibLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let data = userData;

    // Apply search filter
    data = data.filter(
      (row) =>
        row.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.phone.includes(searchTerm)
    );

    // Apply status filter
    if (filterStatus !== 'All') {
      data = data.filter((row) => row.isActive === filterStatus);
    }

    if (selectedGroup) {
      data = data.filter((row) => row.groupId === selectedGroup.groupId);
    }

    return data;
  }, [userData, searchTerm, filterStatus, selectedGroup]);

  const handleRowClick = (id: number) => {
    setSelected(id === selected ? null : id);
  };

  const isSelected = (id: number) => id === selected;

  const handleNextPage = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber((prev) => prev - 1);
    }
  };

  const handleSelectPage = async (pageNumber: any) => {
    setPageNumber(pageNumber);
  }

  const changeSort = async (column: UserSortType) => {
    if (column !== sortColumnName) {
      setSortDesc(false);
    } else {
      setSortDesc(!sortDesc);
    }
    setSortColumnName(column);
  }

  const handleClearSort = async () => {
    setSortColumnName(null);
    setSortDesc(null);
  }

  const hideOnMobileSx = { display: { xs: 'none', md: 'table-cell' } as const };

  const selectGroupFilter = (group: AdminGroupSelectItem | null) => {
    setSelectedGroup(group);
    setPageNumber(1);
    setGroupFilterAnchor(null);
  };

  return (
    <div style={{ padding: 0, backgroundColor: isDarkMode ? '#1e1e1e' : '#f5f5f5' }}>
      <Toaster />
      <div className="admin-toolbar">
        <TextField
          className="admin-search"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: isDarkMode ? '#e0e0e0' : '#666666', opacity: 0.8 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: '1 1 200px',
            minWidth: 0,
            '& .MuiInputBase-root': {
              borderRadius: '4px',
              background: isDarkMode ? '#444444' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#076596',
              fontSize: '0.9rem',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              padding: '2px 10px',
            },
            '& .MuiInputBase-input': { padding: '8px 8px 8px 0' },
          }}
          aria-label="search users"
        />
        { sortColumnName !== null && 
          <Button
            variant="outlined"
            onClick={handleClearSort}
            startIcon={<CloseOutlined />}
            sx={{
              border: '1px solid var(--input-border)',
              background: 'var(--input-bg)',
              color: 'var(--text-color)',
              borderWidth: '1px',
              fontWeight: 500,
              flex: { xs: '1 1 auto', sm: '0 0 auto' },
              '&:hover': { borderColor: '#0a6fb4' },
            }}
          >Clear Sort</Button> }
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            borderColor: '#076596',
            color: isDarkMode ? '#ffffff' : '#076596',
            backgroundColor: isDarkMode ? '#444444' : '#ffffff',
            borderWidth: '1px',
            fontWeight: 500,
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            '&:hover': { borderColor: '#0a6fb4', backgroundColor: '#0a6fb420' },
          }}
          aria-label="filter users by status"
          aria-controls={openFilterMenu ? 'filter-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openFilterMenu ? 'true' : undefined}
        >
          Filter {filterStatus !== 'All' && `(${filterStatus})`}
        </Button>

        {/* Filter Menu */}
        <Menu
          id="filter-menu"
          anchorEl={anchorEl}
          open={openFilterMenu}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{ 'aria-labelledby': 'filter-button' }}
        >
          <MenuItem onClick={() => { setFilterStatus('All'); setPageNumber(1); setAnchorEl(null); }}>
            All Users
          </MenuItem>
          <MenuItem onClick={() => { setFilterStatus('Active'); setPageNumber(1); setAnchorEl(null); }}>
            Active Only
          </MenuItem>
          <MenuItem onClick={() => { setFilterStatus('Inactive'); setPageNumber(1); setAnchorEl(null); }}>
            Inactive Only
          </MenuItem>
        </Menu>
      </div>
      <TableContainer
        className="admin-table-scroll"
        component={Paper}
        sx={{
          backgroundColor: isDarkMode ? '#444444' : '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          border: isDarkMode ? '1px solid #076596' : '1px solid rgba(255, 255, 255, 0.3)',
          maxHeight: { xs: '60vh', md: '70vh' },
          overflow: 'auto',
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="users table" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  backgroundColor: 'var(--accent-color)',
                  color: '#ffffff',
                  fontWeight: 700,
                  padding: '12px 16px',
                  fontSize: '0.95rem',
                  letterSpacing: '0.01em',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <TableCell>ID<IconButton onClick={() => changeSort('id')}> {!sortDesc && sortColumnName === 'id' ? <UpIcon sx={{color: 'white'}}/> : <DownIcon sx={{color: 'white'}}/>}</IconButton></TableCell>
              <TableCell>Username<IconButton onClick={() => changeSort('username')}> {!sortDesc && sortColumnName === 'username' ? <UpIcon sx={{color: 'white'}}/> : <DownIcon sx={{color: 'white'}}/>}</IconButton></TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Name<IconButton onClick={() => changeSort('name')}> {!sortDesc && sortColumnName === 'name' ? <UpIcon sx={{color: 'white'}}/> : <DownIcon sx={{color: 'white'}}/>}</IconButton></TableCell>
              <TableCell sx={hideOnMobileSx}>Phone</TableCell>
              <TableCell>Status<IconButton onClick={() => changeSort('status')}> {!sortDesc && sortColumnName === 'status' ? <UpIcon sx={{color: 'white'}}/> : <DownIcon sx={{color: 'white'}}/>}</IconButton></TableCell>
              <TableCell>
                Group{selectedGroup ? `: ${selectedGroup.groupName}` : ''}
                <IconButton
                  aria-label="filter users by group"
                  aria-haspopup="true"
                  aria-expanded={Boolean(groupFilterAnchor)}
                  onClick={(event) => setGroupFilterAnchor(event.currentTarget)}
                >
                  <DownIcon sx={{ color: 'white' }} />
                </IconButton>
                <Menu
                  anchorEl={groupFilterAnchor}
                  open={Boolean(groupFilterAnchor)}
                  onClose={() => setGroupFilterAnchor(null)}
                  PaperProps={{ sx: { minWidth: 160, maxHeight: 280 } }}
                  MenuListProps={{ dense: true, sx: { py: 0.5 } }}
                >
                  <MenuItem dense sx={{ minHeight: 32, px: 1 }} onClick={() => selectGroupFilter(null)}>
                    <Radio size="small" sx={{ p: 0.5, mr: 0.75 }} checked={selectedGroup === null} />
                    <ListItemText primary="All groups" primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                  {groupSelectItems.map((group) => (
                    <MenuItem key={group.groupId} dense sx={{ minHeight: 32, px: 1 }} onClick={() => selectGroupFilter(group)}>
                      <Radio size="small" sx={{ p: 0.5, mr: 0.75 }} checked={selectedGroup?.groupId === group.groupId} />
                      <ListItemText primary={group.groupName} primaryTypographyProps={{ variant: 'body2' }} />
                    </MenuItem>
                  ))}
                </Menu>
              </TableCell>
              <TableCell sx={hideOnMobileSx}>Prints<IconButton onClick={() => changeSort('printcount')}> {!sortDesc && sortColumnName === 'printcount' ? <UpIcon sx={{color: 'white'}}/> : <DownIcon sx={{color: 'white'}}/>}</IconButton></TableCell>
              <TableCell sx={hideOnMobileSx}>Print Requests<IconButton onClick={() => changeSort('printrequestcount')}> {!sortDesc && sortColumnName === 'printrequestcount' ? <UpIcon sx={{color: 'white'}}/> : <DownIcon sx={{color: 'white'}}/>}</IconButton></TableCell>
              <TableCell align="center">Printer Settings</TableCell>
              <TableCell align="center">Pessure Calibration</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#076596',
                      fontSize: '1rem',
                      fontWeight: 500,
                      padding: '16px',
                    }}
                  >
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : error || filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ff6b6b' : '#e74c3c',
                      fontSize: '1rem',
                      fontWeight: 500,
                      padding: '16px',
                    }}
                  >
                    {error || 'No matching users found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => handleRowClick(row.id)}
                  selected={isSelected(row.id)}
                  sx={{
                    backgroundColor: isSelected(row.id)
                      ? isDarkMode
                        ? '#076596'
                        : '#b8d6ff'
                      : isDarkMode
                        ? '#444444'
                        : '#ffffff',
                    '& .MuiTableCell-root': { backgroundColor: 'transparent !important' },
                    '&:hover': {
                      backgroundColor: isSelected(row.id)
                        ? isDarkMode
                          ? '#0a6fb4'
                          : '#b8d6ff'
                        : isDarkMode
                          ? '#5a5a5a'
                          : '#becfe6',
                    },
                    '& td, & th': {
                      color: isDarkMode ? '#ffffff' : '#076596',
                      padding: '10px 16px',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                    },
                    cursor: 'pointer',
                  }}
                >
                  <TableCell component="th" scope="row">{row.id}</TableCell>
                  <TableCell>
                    {row.username}
                    {row.isAdmin && (
                      <Chip
                        label="Admin"
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{row.email || '—'}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell sx={hideOnMobileSx}>{row.phone}</TableCell>
                  <TableCell>
                    <FormControl sx={{ minWidth: 120 }}>
                      <Select
                        value={row.isActive} // "Active" | "Inactive"
                        onChange={(e) =>
                          handleStatusChange(row.id, e.target.value as "Active" | "Inactive")
                        }
                        onClick={(e) => e.stopPropagation()}
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: selected === "Active" ? "success.main" : "error.main",
                              }}
                            />
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {selected}
                            </Box>
                          </Box>
                        )}
                        sx={(theme) => {
                          const isActive = row.isActive === "Active";
                          const color = isActive ? theme.palette.success.main : theme.palette.error.main;
                          return {
                            height: 32,
                            fontSize: "0.9rem",
                            color,
                            ".MuiSvgIcon-root": { color }, // dropdown arrow
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: color },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: color },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: color },
                          };
                        }}
                        aria-label={`status for user ${row.id}`}
                      >
                        <MenuItem value="Active">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main" }} />
                            Active
                          </Box>
                        </MenuItem>
                        <MenuItem value="Inactive">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main" }} />
                            Inactive
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {
                      isGroupEditEnabled ? 
                        <Select
                          value={row.groupId} className="table-select"
                          onChange={(e) => handleGroupChange(row.id, e.target.value)}
                          sx={{
                            color: 'var(--text-color)',
                            '& fieldset': {
                              borderColor: 'var(--input-border)',
                            }
                          }}
                        >
                          {groupSelectItems.map((group) => (
                            <MenuItem key={group.groupId} value={group.groupId}>
                              {group.groupName}
                            </MenuItem>
                          ))}
                        </Select> :
                        <span>{groupSelectItems.find(gi => gi.groupId === row.groupId)?.groupName ?? ''}</span>
                    }
                  </TableCell>
                  <TableCell sx={hideOnMobileSx}>{row.printCount}</TableCell>
                  <TableCell sx={hideOnMobileSx}>{row.printRequestCount}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      aria-label={`manage printer settings`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDeviceModal(row.id);
                      }}
                      sx={{ color: isDarkMode ? '#ffffff' : '#076596', '&:hover': { color: '#0a6fb4' } }}
                    >
                      <OpenInBrowser fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      aria-label={`manage calibration file`}
                      disabled={row.isAdmin}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenCalibModal(row.id);
                      }}
                      sx={{ color: isDarkMode ? '#ffffff' : '#076596', '&:hover': { color: '#0a6fb4' } }}
                    >
                      <OpenInBrowser fontSize="small" />
                    </IconButton>
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      aria-label={`delete user ${row.id}`}
                      disabled={row.isAdmin || !isDeleteEnabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(row.id);
                      }}
                      sx={{ color: isDarkMode ? '#ffffff' : '#076596', '&:hover': { color: '#e74c3c' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="admin-pagination" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            disabled={pageNumber === 1}
            onClick={handlePreviousPage}
            sx={{
              borderColor: '#076596',
              color: isDarkMode ? '#ffffff' : '#076596',
              backgroundColor: isDarkMode ? '#444444' : '#ffffff',
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
              '&.Mui-disabled': {
                borderColor: isDarkMode ? '#555555' : '#cccccc',
                color: isDarkMode ? '#777777' : '#999999',
              },
            }}
          >
            Prev
          </Button>
          {(() => {
            const total = totalPages.current;
            const pagesToShow: (number | 'ellipsis')[] = [];

            if (total <= 5) {
              for (let i = 1; i <= total; i++) pagesToShow.push(i);
            } else {
              if (pageNumber === 1) pagesToShow.push(1, 2, 'ellipsis', total - 1, total);
              else if (pageNumber < total - 1) pagesToShow.push(pageNumber - 1, pageNumber, 'ellipsis', total - 1, total);
              else if (pageNumber === total - 1) pagesToShow.push(1, 2, 'ellipsis', pageNumber, total)
              else pagesToShow.push(1, 2, 'ellipsis', pageNumber - 1, pageNumber)
            }

            return pagesToShow.map((p, idx) => {
              if (p === 'ellipsis') {
                return (<div key={`ellipsis-${idx}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end' }}> ... </div>);
              }

              const pageIndex = p as number;
              return (
                <Button
                  key={`page-${pageIndex}`}
                  disabled={pageIndex === pageNumber}
                  variant="outlined"
                  onClick={() => handleSelectPage(pageIndex)}
                  sx={{
                    borderColor: '#076596',
                    color: isDarkMode ? '#ffffff' : '#076596',
                    backgroundColor: isDarkMode ? '#444444' : '#ffffff',
                    padding: '8px 16px',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
                    '&.Mui-disabled': {
                      borderColor: isDarkMode ? '#555555' : '#cccccc',
                      color: isDarkMode ? '#777777' : '#999999',
                    },
                  }}
                >
                  {pageIndex}
                </Button>
              );
            });
          })()}
          <Button
            variant="outlined"
            disabled={!hasNextPage}
            onClick={handleNextPage}
            sx={{
              borderColor: '#076596',
              color: isDarkMode ? '#ffffff' : '#076596',
              backgroundColor: isDarkMode ? '#444444' : '#ffffff',
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
              '&.Mui-disabled': {
                borderColor: isDarkMode ? '#555555' : '#cccccc',
                color: isDarkMode ? '#777777' : '#999999',
              },
            }}
          >
            Next
          </Button>
        </div>
        <Typography sx={{ color: isDarkMode ? '#ffffff' : '#076596', fontWeight: 600, whiteSpace: 'nowrap' }}>
          Total users: {loading ? '...' : totalUsers}
        </Typography>
      </div>
      <Modal
        open={openDeviceModal}
        onClose={() => {
          setOpenDeviceModal(false);
          setDeviceName('');
          setConfigFile(null);
          setError(null);
        }}
        aria-labelledby="device-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      >
        <Box
          sx={{
            width: { xs: '94%', sm: 600 },
            bgcolor: isDarkMode ? '#333333' : '#ffffff',
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            borderRadius: '12px',
            border: isDarkMode ? '1px solid #076596' : '1px solid rgba(0, 0, 0, 0.1)',
            maxHeight: '85vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              id="device-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: isDarkMode ? '#e0e0e0' : '#076596', fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Manage Printer Settings
            </Typography>
            <IconButton
              onClick={() => setOpenDeviceModal(false)}
              sx={{ color: isDarkMode ? '#e0e0e0' : '#076596' }}
              aria-label="close modal"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {deviceLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CircularProgress size={24} sx={{ color: '#076596' }} />
            </Box>
          )}
          <Box
            component="form"
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (!selected) return;

              if (isEditMode && editingDeviceId) {
                handleUpdateDevice(selected, editingDeviceId);
              } else {
                handleAddDevice(selected);
              }
            }}
          >
            <TextField
              label="Setting Name"
              value={settingName}
              onChange={(e) => setDeviceName(e.target.value)}
              fullWidth
              required
              error={!settingName}
              helperText={!settingName ? 'name is required' : ''}
              sx={{
                '& .MuiInputBase-input': { color: isDarkMode ? '#e0e0e0' : '#076596' },
                '& .MuiInputLabel-root': { color: isDarkMode ? '#e0e0e0' : '#076596' },
              }}
              aria-label="setting name"
            />
            <TextField
              type="file"
              onChange={(e) => setConfigFile(e.target.files ? e.target.files[0] : null)}
              fullWidth
              required
              error={!configFile}
              helperText={!configFile ? 'setting zip file is required' : ''}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-input': { color: isDarkMode ? '#e0e0e0' : '#076596' },
                '& .MuiInputLabel-root': { color: isDarkMode ? '#e0e0e0' : '#076596' },
              }}
              aria-label="setting zip file"
            />
            <Button
              variant="contained"
              type="submit"
              sx={{
                bgcolor: '#076596',
                '&:hover': { bgcolor: '#0a6fb4' },
                gridColumn: { xs: '1', sm: '1 / 3' },
                mt: 1,
              }}
              disabled={!settingName || !configFile || deviceLoading}
              aria-label="Ok"
            >
               {isEditMode ? 'Update' : 'Add'}
            </Button>
          </Box>
          <TableContainer component={Paper} className="admin-table-scroll" sx={{ bgcolor: isDarkMode ? '#444444' : '#ffffff', overflowX: 'auto' }}>
            <Table aria-label="settings table" sx={{ minWidth: 360 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      backgroundColor: isDarkMode ? '#076596' : '#2596be',
                      color: '#ffffff',
                      fontWeight: 600,
                    },
                  }}
                >
                  <TableCell>Printer Setting ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedUserPrinterSettings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: isDarkMode ? '#e0e0e0' : '#076596' }}>
                      No printer settings available
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedUserPrinterSettings.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        '& td': { color: isDarkMode ? '#e0e0e0' : '#076596' },
                        '&:hover': { bgcolor: isDarkMode ? '#5a5a5a' : '#becfe6' },
                      }}
                    >
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => selected && handleDeleteDevice(selected, item.id)}
                          sx={{ color: isDarkMode ? '#e0e0e0' : '#076596', '&:hover': { color: '#e74c3c' } }}
                          aria-label={`delete setting ${item.id}`}
                          disabled={deviceLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => selected && handleEditDevice(selected, item.id)}
                          sx={{ color: isDarkMode ? '#e0e0e0' : '#076596', '&:hover': { color: '#e74c3c' } }}
                          aria-label={`edit setting ${item.id}`}
                          disabled={deviceLoading}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>

      <Modal
        open={openCalibModal}
        onClose={() => {
          setOpenCalibModal(false);
          setCalibName('');
          setCalibFile(null);
          setError(null);
        }}
        aria-labelledby="device-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      >
        <Box
          sx={{
            width: { xs: '94%', sm: 600 },
            bgcolor: isDarkMode ? '#333333' : '#ffffff',
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            borderRadius: '12px',
            border: isDarkMode ? '1px solid #076596' : '1px solid rgba(0, 0, 0, 0.1)',
            maxHeight: '85vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              id="device-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: isDarkMode ? '#e0e0e0' : '#076596', fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Manage Pressure Calibration
            </Typography>
            <IconButton
              onClick={() => setOpenCalibModal(false)}
              sx={{ color: isDarkMode ? '#e0e0e0' : '#076596' }}
              aria-label="close modal"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {calibLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CircularProgress size={24} sx={{ color: '#076596' }} />
            </Box>
          )}
          <Box
            component="form"
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}
            onSubmit={(e) => {
              e.preventDefault();
              selected && handleAddCalib(selected);
            }}
          >
            <TextField
              label="Calibration Name"
              value={calibName}
              onChange={(e) => setCalibName(e.target.value)}
              fullWidth
              required
              error={!calibName}
              helperText={!calibName ? 'name is required' : ''}
              sx={{
                '& .MuiInputBase-input': { color: isDarkMode ? '#e0e0e0' : '#076596' },
                '& .MuiInputLabel-root': { color: isDarkMode ? '#e0e0e0' : '#076596' },
              }}
              aria-label="calibration name"
            />
            <TextField
              type="file"
              onChange={(e) => setCalibFile(e.target.files ? e.target.files[0] : null)}
              fullWidth
              required
              error={!calibFile}
              helperText={!calibFile ? 'calibration zip file is required' : ''}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-input': { color: isDarkMode ? '#e0e0e0' : '#076596' },
                '& .MuiInputLabel-root': { color: isDarkMode ? '#e0e0e0' : '#076596' },
              }}
              aria-label="setting zip file"
            />
            <Button
              variant="contained"
              type="submit"
              sx={{
                bgcolor: '#076596',
                '&:hover': { bgcolor: '#0a6fb4' },
                gridColumn: { xs: '1', sm: '1 / 3' },
                mt: 1,
              }}
              disabled={!calibName || !calibFile || calibLoading}
              aria-label="Ok"
            >
              Add
            </Button>
          </Box>
          <TableContainer component={Paper} className="admin-table-scroll" sx={{ bgcolor: isDarkMode ? '#444444' : '#ffffff', overflowX: 'auto' }}>
            <Table aria-label="Calibration table" sx={{ minWidth: 360 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      backgroundColor: isDarkMode ? '#076596' : '#2596be',
                      color: '#ffffff',
                      fontWeight: 600,
                    },
                  }}
                >
                  <TableCell>Pressure Calibration ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!selectedUserCalib || !selectedUserCalib.userDeviceId || !selectedUserCalib.name) ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: isDarkMode ? '#e0e0e0' : '#076596' }}>
                      No pressure calibration available
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow
                    key={selectedUserCalib.userDeviceId}
                    sx={{
                      '& td': { color: isDarkMode ? '#e0e0e0' : '#076596' },
                      '&:hover': { bgcolor: isDarkMode ? '#5a5a5a' : '#becfe6' },
                    }}
                  >
                    <TableCell>{selectedUserCalib.userDeviceId}</TableCell>
                    <TableCell>{selectedUserCalib.name}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => selected && selectedUserCalib?.userDeviceId != null && handleDeleteCalib(selected, selectedUserCalib.userDeviceId)}
                        sx={{ color: isDarkMode ? '#e0e0e0' : '#076596', '&:hover': { color: '#e74c3c' } }}
                        aria-label={`delete pressure calibration ${selectedUserCalib?.userDeviceId}`}
                        disabled={calibLoading || !selectedUserCalib?.userDeviceId}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
    </div>
  );
};

export default OwnersTable;
